import React from 'react';
import { Modal, Stack, Group, Badge, SegmentedControl, Checkbox, Card, Text, Button, ColorInput } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { DataQuery } from '../../types';
import type { PivotedMeasurementRecord } from '../../types/pivoted';

interface Props {
  opened: boolean;
  onClose: () => void;
  // i18n
  t: TFunction<'translation'>;
  // context
  query: DataQuery; // read-only usage for canCandle calc; not stored
  data: PivotedMeasurementRecord[];
  // metric settings
  selectedMetrics: string[];
  metricSettings: Record<string, { color: string; axis: 'left' | 'right' | 'both'; type: 'line' | 'boxplot' | 'step' | 'arearange'; trendColor?: string; showTrend?: boolean }>;
  setMetricSettings: React.Dispatch<React.SetStateAction<Record<string, { color: string; axis: 'left' | 'right' | 'both'; type: 'line' | 'boxplot' | 'step' | 'arearange'; trendColor?: string; showTrend?: boolean }>>>;
  // split toggles
  splitByMetric: boolean;
  setSplitByMetric: (v: boolean) => void;
  splitByDevice: boolean;
  setSplitByDevice: (v: boolean) => void;
  splitByGateway: boolean;
  setSplitByGateway: (v: boolean) => void;
  // weather shapes
  weatherMetrics: string[];
  weatherShapes: Record<string, 'line' | 'step' | 'bar'>;
  setWeatherShapes: React.Dispatch<React.SetStateAction<Record<string, 'line' | 'step' | 'bar'>>>;
  translateMetricId: (id: string) => string;
  // unit axis sides
  availableUnits: string[];
  unitAxisSides: Record<string, 'left' | 'right'>;
  setUnitAxisSides: React.Dispatch<React.SetStateAction<Record<string, 'left' | 'right'>>>;
  // trends
  showTrends: boolean;
  setShowTrends: (v: boolean) => void;
  // trendTargets intentionally not exposed in UI anymore; always 'base'
}

const DataChartSettingsDialog: React.FC<Props> = ({
  opened,
  onClose,
  t,
  query,
  data,
  selectedMetrics,
  metricSettings,
  setMetricSettings,
  splitByMetric,
  setSplitByMetric,
  splitByDevice,
  setSplitByDevice,
  splitByGateway,
  setSplitByGateway,
  weatherMetrics,
  weatherShapes,
  setWeatherShapes,
  translateMetricId,
  availableUnits,
  unitAxisSides,
  setUnitAxisSides,
  showTrends,
  setShowTrends,
}) => {
  // helpers for canCandle
  const sensorIds: string[] = Array.isArray(query.sensorId)
    ? (query.sensorId as string[])
    : (query.sensorId ? [query.sensorId as string] : []);
  const derivedSensors: string[] = Array.from(new Set((data || []).map((r) => r?.sensor?.id).filter(Boolean) as string[])).slice(0, 10);
  const effectiveSensorIds: string[] = sensorIds.length > 0 ? sensorIds : derivedSensors;

  return (
    <Modal opened={opened} onClose={onClose} title={t('data.chart.settings') || 'Diagramm-Einstellungen'} centered>
      <Stack>
        <Group>
          <Checkbox
            label={t('data.chart.splitByMetric') || 'Nach Metrik aufteilen'}
            checked={splitByMetric}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByMetric(e.currentTarget.checked)}
          />
          <Checkbox
            label={t('data.chart.splitByDevice') || 'Nach Gerät aufteilen (Gateway/Sensor)'}
            checked={splitByDevice}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByDevice(e.currentTarget.checked)}
          />
          <Checkbox
            label={t('data.chart.splitByGateway') || 'Nach Gateway aufteilen'}
            checked={splitByGateway}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSplitByGateway(e.currentTarget.checked)}
          />
        </Group>

        {/* Per-line settings grouped by sensor */}
        <Stack>
          {effectiveSensorIds.map((sensorId) => {
            return (
              <div key={`sensor-${sensorId}`}>
                <Text size="sm" fw={600} mb={8}>{sensorId}</Text>
                <Stack gap={6} ml="md">
                  {selectedMetrics.length > 0 ? (
                    selectedMetrics.map((m) => {
                      const lineKey = `${m} (${sensorId})`;
                      return (
                        <Card key={`settings-${lineKey}`} withBorder p="sm">
                          <Stack gap="sm">
                            <Group justify="space-between" align="center">
                              <Group gap="xs" align="center">
                                <Badge variant="light">{translateMetricId(m)}</Badge>
                                <ColorInput
                                  size="xs"
                                  value={metricSettings[lineKey]?.color || metricSettings[m]?.color || '#3b82f6'}
                                  onChange={(val) => setMetricSettings((prev) => ({
                                    ...prev,
                                    [lineKey]: { color: val || '#3b82f6', axis: prev[lineKey]?.axis ?? prev[m]?.axis ?? 'left', type: prev[lineKey]?.type ?? prev[m]?.type ?? 'line', trendColor: prev[lineKey]?.trendColor },
                                  }))}
                                  format="hex"
                                  withPicker
                                  swatches={[
                                    '#ef4444','#f97316','#f59e0b','#10b981','#3b82f6','#8b5cf6','#06b6d4','#84cc16','#f43f5e','#a855f7',
                                  ]}
                                />
                              </Group>
                              <SegmentedControl
                                value={metricSettings[lineKey]?.type || metricSettings[m]?.type || 'line'}
                                onChange={(v: string) => setMetricSettings((prev) => {
                                  const next = { ...prev } as typeof prev;
                                  const newType = v as 'line' | 'boxplot' | 'step' | 'arearange';
                                  if (!next[lineKey]) next[lineKey] = { color: prev[lineKey]?.color || prev[m]?.color || '#8884d8', axis: prev[lineKey]?.axis || prev[m]?.axis || 'left', type: 'line' };
                                  next[lineKey] = { ...next[lineKey], type: newType };
                                  return next;
                                })}
                                data={[
                                  { label: 'Boxplot', value: 'boxplot' },
                                  { label: 'Linie', value: 'line' },
                                  { label: 'Stufe', value: 'step' },
                                  { label: 'Area Range', value: 'arearange' },
                                ]}
                                size="sm"
                              />
                            </Group>
                            {/* Trendline color for this line */}
                            {showTrends && (
                              <Group gap="sm" align="center">
                                <Text size="xs" c="dimmed">Trendlinie:</Text>
                                <ColorInput
                                  size="xs"
                                  placeholder="Trendlinien-Farbe"
                                  value={metricSettings[lineKey]?.trendColor || metricSettings[m]?.trendColor || '#999999'}
                                  onChange={(val) => setMetricSettings((prev) => ({
                                    ...prev,
                                    [lineKey]: { ...prev[lineKey], trendColor: val || '#999999', color: prev[lineKey]?.color || prev[m]?.color || '#3b82f6', axis: prev[lineKey]?.axis ?? prev[m]?.axis ?? 'left', type: prev[lineKey]?.type ?? prev[m]?.type ?? 'line' },
                                  }))}
                                  format="hex"
                                  withPicker
                                  swatches={[
                                    '#ef4444','#f97316','#f59e0b','#10b981','#3b82f6','#8b5cf6','#06b6d4','#84cc16','#f43f5e','#a855f7',
                                  ]}
                                />
                              </Group>
                            )}
                          </Stack>
                        </Card>
                      );
                    })
                  ) : (
                    <Text size="xs" c="dimmed">{t('common.none') || 'Keine Metriken'}</Text>
                  )}
                </Stack>
              </div>
            );
          })}

          {weatherMetrics.length > 0 && (
            <Card withBorder p="sm">
              <Group justify="space-between" align="center">
                <Group gap="xs" align="center">
                  <Badge variant="light">{t('data.filters.weatherMetrics') || 'Zusatz-Metriken (Wetter)'}</Badge>
                </Group>
                <Group gap="sm" wrap="wrap">
                  {(['weather.temperature','weather.humidity','weather.precipitation'] as const)
                    .filter((wm) => weatherMetrics.includes(wm))
                    .map((wm) => (
                      <Group key={`wm-${wm}`} gap="xs" align="center">
                        <Badge variant="outline">{t(wm) || wm}</Badge>
                        <ColorInput
                          size="xs"
                          value={metricSettings[wm]?.color || '#64748b'}
                          onChange={(val) => setMetricSettings((prev) => ({
                            ...prev,
                            [wm]: { color: val || '#64748b', axis: prev[wm]?.axis ?? 'left', type: prev[wm]?.type ?? 'line' },
                          }))}
                          format="hex"
                          withPicker
                          swatches={[
                            '#ef4444','#f97316','#f59e0b','#10b981','#3b82f6','#8b5cf6','#06b6d4','#84cc16','#f43f5e','#a855f7',
                          ]}
                        />
                        <SegmentedControl
                          value={weatherShapes[wm] || 'line'}
                          onChange={(v: string) => setWeatherShapes((prev) => ({ ...prev, [wm]: v as 'line' | 'step' | 'bar' }))}
                          data={[
                            { label: 'Line', value: 'line' },
                            { label: 'Step', value: 'step' },
                            { label: 'Bar', value: 'bar' },
                          ]}
                          size="xs"
                        />
                      </Group>
                    ))}
                </Group>
              </Group>
            </Card>
          )}

          {/* Trendlines */}
          <Card withBorder p="sm">
            <Stack gap={8}>
              <Group justify="space-between" align="center">
                <Badge variant="light">{t('data.chart.trendlines') || 'Trendlinien'}</Badge>
              </Group>
              <Group gap="md" align="center" wrap="wrap">
                <Checkbox
                  label={t('data.chart.trendsShow') || 'Trendlinien anzeigen'}
                  checked={!!showTrends}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowTrends(e.currentTarget.checked)}
                />
                {/* Trendziel ist fest auf 'base' eingestellt; keine Auswahl im UI */}
              </Group>
            </Stack>
          </Card>

          {/* Axis side per unit */}
          <Card withBorder p="sm">
            <Stack gap={8}>
              <Group justify="space-between" align="center">
                <Badge variant="light">{t('data.chart.axisPerUnit') || 'Achsenseite pro Einheit'}</Badge>
              </Group>
              {availableUnits.length > 0 ? (
                <Stack gap={6}>
                  {availableUnits.map((u, idx) => (
                    <Group key={u || `unit-${idx}`} justify="space-between" align="center">
                      <Text fw={600}>{u || (t('data.chart.unit.unknown') || 'Einheit')}</Text>
                      <SegmentedControl
                        value={(unitAxisSides[u] ?? (idx === 0 ? 'left' : 'right'))}
                        onChange={(val) => setUnitAxisSides((prev) => ({ ...prev, [u]: val as 'left' | 'right' }))}
                        data={[
                          { label: t('common.left') || 'Links', value: 'left' },
                          { label: t('common.right') || 'Rechts', value: 'right' },
                        ]}
                        size="xs"
                      />
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed">{t('data.chart.noUnits') || 'Keine Einheiten für aktuelle Auswahl'}</Text>
              )}
              <Group justify="flex-end" mt="xs">
                <Button variant="default" onClick={() => setUnitAxisSides(() => ({}))}>{t('common.reset') || 'Zurücksetzen'}</Button>
                <Button onClick={onClose}>{t('common.close') || 'Schließen'}</Button>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default DataChartSettingsDialog;
